"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Vision() {
  const materials = ["MODERN RESIDENTIAL", "CONTEMPORARY DESIGN", "CHALLENGING LOTS", "INVESTMENT PROPERTIES", "PERMIT-READY PLANS", "TRANSPARENT PRICING"];
  return <section data-section-id="355" id="vision-precision" className="bg-background text-foreground relative overflow-hidden min-h-[90vh] flex items-center">
      {/* Noise overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="relative w-full max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 lg:py-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">

          {/* Left column */}
          <div className="lg:col-span-5">

            {/* Eyebrow */}
            <div data-hero-item="left" className="flex items-center gap-3 mb-8" style={{
            animationDelay: "120ms"
          }}>
              <span className="h-px w-8" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
              <span className="font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase" style={{
              color: "var(--primary)"
            }}>
                Acumen D&B — Knoxville Architecture
              </span>
            </div>

            {/* Heading */}
            <h1 data-hero-item="right" className="font-default font-bold text-foreground leading-[0.95] tracking-tight mb-8" style={{
            fontSize: "clamp(3.5rem, 7vw, 6.5rem)",
            animationDelay: "360ms"
          }}>
              Affordable, high-quality{" "}
              <span className="italic font-normal" style={{
              color: "var(--primary)"
            }}>
                residential design
              </span>
            </h1>

            {/* Subheading */}
            <p data-hero-item="left" className="font-default text-[18px] md:text-[19px] leading-relaxed text-muted-foreground max-w-md mb-10" style={{
            animationDelay: "600ms"
          }}>
              Professional, contemporary architecture tailored to your lifestyle. We turn your vision into reality with precision.
            </p>

            {/* Buttons */}
            <div data-hero-item="right" className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8" style={{
            animationDelay: "1000ms"
          }}>
              <Button asChild size="lg" className="font-default text-base px-8 py-6 h-auto tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg">
                <Link to="/HomePage">Start Your Project</Link>
              </Button>

              <Link to="#portfolio" className="font-default text-base text-foreground hover:text-foreground/70 transition-colors inline-flex items-center gap-2 group border-b border-border hover:border-foreground/40 pb-1">
                View our portfolio
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {/* Serving line */}
            <div data-hero-item="left" className="hidden lg:flex items-center gap-4 mt-16" style={{
            animationDelay: "1500ms"
          }}>
              <span className="h-px w-12" style={{
              backgroundColor: "var(--primary)",
              opacity: 0.5
            }} aria-hidden="true" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{
              color: "var(--muted-foreground)"
            }}>
                Serving Knoxville & Beyond
              </span>
            </div>
          </div>

          {/* Right column */}
          <div data-hero-item="right" className="lg:col-span-7 relative" style={{
          animationDelay: "200ms"
        }}>
            <div className="relative flex justify-center lg:justify-end">
              <div className="hidden md:flex absolute -right-2 lg:right-0 top-1/2 -translate-y-1/2 translate-x-6 lg:translate-x-10 items-center gap-3 z-10">
                <div className="h-24 w-px" style={{
                backgroundColor: "var(--primary)"
              }} aria-hidden="true" />
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase whitespace-nowrap" style={{
                color: "var(--primary)",
                writingMode: "vertical-rl",
                transform: "rotate(180deg)"
              }}>
                  Scroll to explore
                </span>
              </div>

              <div className="relative w-full max-w-[560px] aspect-[3/4] overflow-hidden rounded-lg shadow-lg">
                <div className="absolute top-0 left-0 w-10 h-px z-20" style={{
                backgroundColor: "var(--primary)"
              }} />
                <div className="absolute top-0 left-0 h-10 w-px z-20" style={{
                backgroundColor: "var(--primary)"
              }} />
                <div className="absolute bottom-0 right-0 w-10 h-px z-20" style={{
                backgroundColor: "var(--primary)"
              }} />
                <div className="absolute bottom-0 right-0 h-10 w-px z-20" style={{
                backgroundColor: "var(--primary)"
              }} />

                <img src="https://certain-kite-1.10web.cloud/wp-content/uploads/2026/06/gigapixel-AI_homepage-scaled.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711307/img/modern_residential_facade.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711307/img/modern_residential_facade.webp-wvc-sizes" alt="A contemporary residential home design with clean lines and modern materials" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

                <div className="absolute inset-0 pointer-events-none" style={{
                background: "linear-gradient(to bottom, transparent 60%, hsla(220, 14%, 10%, 0.35) 100%)"
              }} aria-hidden="true" />

                <div className="absolute bottom-5 left-5 z-20">
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{
                  color: "var(--background)"
                }}>
                    Project N°01 — Contemporary Residence
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div data-hero-item="left" className="mt-16 lg:mt-20 relative overflow-hidden border-t border-border pt-6" style={{
        animationDelay: "600ms"
      }}>
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{
            color: "var(--primary)"
          }}>
              Services
            </span>
            <span className="h-px flex-1" style={{
            backgroundColor: "var(--border)"
          }} aria-hidden="true" />
          </div>

          <div className="relative overflow-hidden">
            <div className="flex gap-12 animate-[ticker_40s_linear_infinite] whitespace-nowrap">
              {[...materials, ...materials, ...materials].map((material, i) => <div key={i} className="flex items-center gap-12 shrink-0">
                  <span className="font-mono text-[11px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">
                    {material}
                  </span>
                  <span className="h-1 w-1 rounded-lg shrink-0" style={{
                backgroundColor: "var(--primary)"
              }} aria-hidden="true" />
                </div>)}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </section>;
}